if (!customElements.get('td-product-upsell')) {
  class TdProductUpsell extends HTMLElement {
    static #DEFAULT_VALUE = 'standard';
    static #STORAGE_PREFIX = 'td-upsell:';

    connectedCallback() {
      this.#cards = Array.from(this.querySelectorAll('[data-upsell-card]'));
      this.#popupCards = Array.from(this.querySelectorAll('[data-upsell-popup-card]'));
      this.#input = this.querySelector('[data-upsell-input]');
      this.#popupRoot = this.querySelector('[data-upsell-popup-root]');
      this.#diffTrigger = this.querySelector('[data-diff-open]');
      this.#popupDialog = this.querySelector('.td-product-upsell__popup-dialog');
      this.#onKeydown = this.#handleKeydown.bind(this);

      if (this.#popupRoot && this.#popupRoot.parentElement !== document.body) {
        document.body.appendChild(this.#popupRoot);
      }

      this.#bindCards();
      this.#bindDiffPopup();
      this.#restoreStoredSelection();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.#onKeydown);
      this.#unlockScroll();

      if (this.#popupRoot?.parentElement === document.body) {
        this.#popupRoot.remove();
      }
    }

    #cards = [];
    #popupCards = [];
    #input = null;
    #popupRoot = null;
    #diffTrigger = null;
    #popupDialog = null;
    #onKeydown = null;

    #bindCards() {
      this.#cards.forEach((card) => {
        card.addEventListener('click', () => {
          this.#selectCard(card);
        });
      });
      this.#bindRadioKeys(this.#cards, (card) => this.#selectCard(card));
    }

    #bindDiffPopup() {
      if (!this.#popupRoot) return;

      this.#diffTrigger?.addEventListener('click', () => {
        this.#openDiffPopup();
      });

      this.#popupRoot.querySelectorAll('[data-popup-close]').forEach((button) => {
        button.addEventListener('click', () => {
          this.#closeDiffPopup();
        });
      });

      this.#popupCards.forEach((card) => {
        card.addEventListener('click', () => {
          this.#selectPopupCard(card);
        });
      });
      this.#bindRadioKeys(this.#popupCards, (card) => this.#selectPopupCard(card));

      this.#popupRoot.querySelector('[data-diff-continue]')?.addEventListener('click', () => {
        this.#closeDiffPopup();
      });
    }

    #openDiffPopup() {
      if (!this.#popupRoot) return;

      const selectedMainCard = this.querySelector('[data-upsell-card][aria-checked="true"]');

      if (selectedMainCard) {
        const popupCard = this.#popupCards.find(
          (item) => item.dataset.upsellValue === selectedMainCard.dataset.upsellValue
        );

        if (popupCard) {
          this.#syncPopupSelection(popupCard);
        }
      }

      this.#popupRoot.hidden = false;
      this.#diffTrigger?.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.#onKeydown);
      this.#lockScroll();
      this.#popupDialog?.focus();
    }

    #closeDiffPopup() {
      if (!this.#popupRoot) return;

      this.#popupRoot.hidden = true;
      this.#diffTrigger?.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.#onKeydown);
      this.#unlockScroll();
      this.#diffTrigger?.focus();
    }

    #handleKeydown(event) {
      if (event.key === 'Escape' && this.#popupRoot && !this.#popupRoot.hidden) {
        this.#closeDiffPopup();
      }
    }

    #lockScroll() {
      document.documentElement.classList.add('lock');
    }

    #unlockScroll() {
      if (!document.querySelector('.td-product-upsell__popup-root:not([hidden])')) {
        document.documentElement.classList.remove('lock');
      }
    }

    #selectCard(card) {
      const value = card.dataset.upsellValue;
      if (!value) return;

      this.#cards.forEach((item) => {
        const isSelected = item === card;
        item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        item.tabIndex = isSelected ? 0 : -1;
      });

      if (this.#input) {
        this.#input.value = value;
      }

      this.#persistSelection(value);

      this.dispatchEvent(
        new CustomEvent('td:upsell-change', {
          bubbles: true,
          detail: {
            value,
            variantId: card.dataset.variantId || null
          }
        })
      );
    }

    /**
     * Called when the user picks a card inside the "what's the difference" popup. Mirrors the
     * choice back onto the main cards immediately, so the two stay in sync without requiring the
     * "Continue" button (which now only closes the popup).
     */
    #selectPopupCard(card) {
      const value = card.dataset.upsellValue;
      if (!value) return;

      this.#syncPopupSelection(card);

      const mainCard = this.querySelector(`[data-upsell-card][data-upsell-value="${value}"]`);

      if (mainCard) {
        this.#selectCard(mainCard);
      }
    }

    /**
     * Updates only the popup's own UI, without propagating back to the main cards. Used to mirror
     * an already-applied main selection into the popup (e.g. on open, or on restore).
     */
    #syncPopupSelection(card) {
      this.#popupCards.forEach((item) => {
        const isSelected = item === card;
        item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        item.tabIndex = isSelected ? 0 : -1;
      });
    }

    #bindRadioKeys(cards, select) {
      cards.forEach((card, index) => {
        card.addEventListener('keydown', (event) => {
          const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
          const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
          if (!forward && !backward) return;

          event.preventDefault();
          const delta = forward ? 1 : -1;
          const next = cards[(index + delta + cards.length) % cards.length];
          select(next);
          next.focus();
        });
      });
    }

    #getStorageKey() {
      const productId = this.dataset.productId || window.location.pathname;
      return `${TdProductUpsell.#STORAGE_PREFIX}${productId}`;
    }

    #readStoredValue() {
      try {
        return sessionStorage.getItem(this.#getStorageKey());
      } catch {
        return null;
      }
    }

    #persistSelection(value) {
      try {
        const key = this.#getStorageKey();

        if (!value || value === TdProductUpsell.#DEFAULT_VALUE) {
          sessionStorage.removeItem(key);
          return;
        }

        sessionStorage.setItem(key, value);
      } catch {
        // sessionStorage unavailable (e.g. private browsing)
      }
    }

    #restoreStoredSelection() {
      const storedValue = this.#readStoredValue();

      if (!storedValue || storedValue === TdProductUpsell.#DEFAULT_VALUE) {
        return;
      }

      const card = this.#cards.find((item) => item.dataset.upsellValue === storedValue);

      if (!card) {
        this.#persistSelection(TdProductUpsell.#DEFAULT_VALUE);
        return;
      }

      this.#selectCard(card);

      const popupCard = this.#popupCards.find((item) => item.dataset.upsellValue === storedValue);

      if (popupCard) {
        this.#syncPopupSelection(popupCard);
      }
    }
  }

  customElements.define('td-product-upsell', TdProductUpsell);
}

/**
 * Keeps the add-to-cart buttons in sync with the upsell selection, and adds the selected add-on
 * product to the cart alongside the main product.
 *
 * The default "standard" card is not a product, so it only ever changes the displayed price back
 * to the bare variant price.
 */
(() => {
  const DEFAULT_VALUE = 'standard';

  const getUpsell = () => document.querySelector('[data-td-product-upsell]');

  const getSelectedCard = () => getUpsell()?.querySelector('[data-upsell-card][aria-checked="true"]') ?? null;

  const getUpsellForm = () => {
    const formId = getUpsell()?.dataset.formId;

    return formId ? document.forms[formId] ?? null : null;
  };

  const getAtcButtons = (form) => {
    if (!form) return [];

    return Array.from(form.elements).filter((element) => element.matches?.('[data-td-product-atc]'));
  };

  const syncAtcPrice = () => {
    const value = getSelectedCard()?.dataset.upsellValue || DEFAULT_VALUE;

    getAtcButtons(getUpsellForm()).forEach((button) => {
      const priceElements = Array.from(button.querySelectorAll('[data-td-atc-price]'));
      if (priceElements.length === 0) return;

      const hasValue = priceElements.some((element) => element.dataset.tdAtcPrice === value);
      const visibleValue = hasValue ? value : DEFAULT_VALUE;

      priceElements.forEach((element) => {
        element.hidden = element.dataset.tdAtcPrice !== visibleValue;
      });
    });
  };

  const addSelectedAddonToCart = async (card, form) => {
    const payload = {
      id: Number(card.dataset.variantId),
      quantity: Number(form.elements.quantity?.value) || 1
    };

    if (card.dataset.sellingPlanId) {
      payload.selling_plan = parseInt(card.dataset.sellingPlanId, 10);
    }

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.description || response.statusText);
    }
  };

  let isResubmitting = false;
  let isAddingAddon = false;

  /**
   * Runs on `document` in the capture phase so it always beats the `submit` listener that
   * `product-form` binds on the form itself. The add-on goes in first so the cart sections bundled
   * with the main product response already contain both line items.
   */
  const onSubmitCapture = async (event) => {
    if (isResubmitting) return;

    const form = event.target;
    if (form !== getUpsellForm()) return;

    const card = getSelectedCard();
    if (!card?.dataset.variantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (isAddingAddon) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const { submitter } = event;
    const submitButtons = Array.from(form.elements).filter((element) => element.type === 'submit');
    submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    isAddingAddon = true;

    try {
      await addSelectedAddonToCart(card, form);
    } catch (error) {
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error.message } }));
      return;
    } finally {
      isAddingAddon = false;
      submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
    }

    isResubmitting = true;

    try {
      form.requestSubmit(submitter?.form === form ? submitter : undefined);
    } finally {
      isResubmitting = false;
    }
  };

  document.addEventListener('submit', onSubmitCapture, true);
  document.addEventListener('td:upsell-change', syncAtcPrice);

  // Variant changes re-render the buy buttons and the sticky bar from Liquid, wiping the upsell price.
  document.addEventListener('product:rerender', () => requestAnimationFrame(syncAtcPrice), true);
  document.addEventListener('shopify:section:load', syncAtcPrice);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAtcPrice);
  } else {
    syncAtcPrice();
  }
})();
